import React from "react";
import "./Login.css";
import { withRouter } from "react-router-dom";
import Axios from "axios";
import { MsalContext } from "@azure/msal-react";
import { getToken, setToken, verifyToken } from "../../helpers/signJwtToken";
import { AUTH_MESSAGE_KEY } from "../../helpers/apiAuth";

const config = require("../../config/config");

const MicrosoftIcon = () => (
  <svg
    className="tw-mr-2 tw-h-4 tw-w-4"
    viewBox="0 0 21 21"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

class Login extends React.Component {
  static contextType = MsalContext;
  msalInstance = null;
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      error: "",
      notice: "",
    };
    this.msalInstance = this.props.msalContext.instance;
    this.microsoftAuthLogin = this.microsoftAuthLogin.bind(this);
  }

  async componentDidMount() {
    // Message left by an expired session (see helpers/apiAuth.js)
    try {
      const notice = sessionStorage.getItem(AUTH_MESSAGE_KEY);
      if (notice) {
        sessionStorage.removeItem(AUTH_MESSAGE_KEY);
        this.setState({ notice });
      }
    } catch (e) {
      // ignore
    }
    // Already signed in with a valid API session → straight to the dashboard
    try {
      await verifyToken(getToken());
      this.props.history.replace("/dashboard");
    } catch (e) {
      // not signed in
    }
  }

  onLoginError(message) {
    this.setState({
      isLoading: false,
      error: message || "An error occurred while signing in. Please try again.",
    });
  }

  // Microsoft ID token: reuse the signed-in Microsoft account when possible,
  // otherwise (or when forced) open the Microsoft sign-in popup
  async getMicrosoftIdToken(forcePopup = false) {
    const request = { scopes: ["openid", "profile"] };
    await this.msalInstance.handleRedirectPromise().catch(() => null);
    const account = this.msalInstance.getActiveAccount() || this.msalInstance.getAllAccounts()[0];
    if (account && !forcePopup) {
      try {
        const result = await this.msalInstance.acquireTokenSilent({ ...request, account });
        if (result && result.idToken) return result.idToken;
      } catch (e) {
        // fall back to the popup
      }
    }
    const result = await this.msalInstance.loginPopup(request);
    if (result && result.account) this.msalInstance.setActiveAccount(result.account);
    return result.idToken;
  }

  // Exchange the Microsoft ID token for an API session token
  exchangeToken(idToken) {
    return Axios.post(config.API_BASE + "/auth/microsoft", { idToken })
      .then((res) => res.data)
      .catch((err) =>
        err && err.response && err.response.data
          ? err.response.data
          : { status: false, message: "Couldn't reach the server. Please try again." },
      );
  }

  async microsoftAuthLogin() {
    this.setState({ isLoading: true, error: "", notice: "" });
    try {
      let result = await this.exchangeToken(await this.getMicrosoftIdToken());
      // A cached Microsoft token can be stale — sign in again once
      if (!result.status && result.code === "MS_TOKEN_INVALID") {
        result = await this.exchangeToken(await this.getMicrosoftIdToken(true));
      }
      if (result.status && result.token) {
        setToken(result.token);
        this.props.history.replace("/dashboard");
      } else {
        this.onLoginError(result.message);
      }
    } catch (err) {
      console.log(err);
      // Closing the Microsoft popup is not an error worth showing
      if (err && err.errorCode === "user_cancelled") {
        this.setState({ isLoading: false });
      } else {
        this.onLoginError();
      }
    }
  }

  render() {
    const { isLoading, error, notice } = this.state;

    // `tw-font-sans` is repeated on <p> because Login.css sets a global
    // font-family on p, which would otherwise override the inherited font.
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-gradient-subtle tw-p-6 tw-font-sans">
        <div className="tw-w-full tw-max-w-md">
          {/* Logo/Brand */}
          <div className="tw-mb-8 tw-text-center">
            <div className="tw-mb-4 tw-inline-flex tw-items-center tw-justify-center">
              <img
                src="/images/gmi_logo.png"
                alt="Great Manager Institute"
                className="tw-h-16 tw-w-auto"
              />
            </div>
            <h1 className="tw-m-0 tw-text-2xl tw-font-bold tw-text-foreground">
              Attendance Tracker
            </h1>
            <p className="tw-mt-2 tw-font-sans tw-text-base tw-text-muted-foreground">
              Sign in to your account
            </p>
          </div>

          <div className="tw-rounded-lg tw-border tw-border-solid tw-border-border/50 tw-bg-card tw-text-card-foreground tw-shadow-strong">
            <div className="tw-flex tw-flex-col tw-space-y-1.5 tw-p-6">
              <h2 className="tw-m-0 tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight">
                Welcome back
              </h2>
              <p className="tw-font-sans tw-text-sm tw-text-muted-foreground">
                Sign in with your GMI Microsoft account to access your
                attendance dashboard
              </p>
            </div>
            <div className="tw-p-6 tw-pt-0">
              {notice && (
                <div
                  className="tw-mb-3 tw-rounded-md tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-center tw-font-sans tw-text-sm tw-text-amber-900"
                  role="status"
                >
                  {notice}
                </div>
              )}
              <button
                type="button"
                onClick={this.microsoftAuthLogin}
                disabled={isLoading}
                className="tw-inline-flex tw-h-10 tw-w-full tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-md tw-border-0 tw-bg-gradient-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-primary-foreground tw-transition-opacity hover:tw-opacity-90 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2 disabled:tw-pointer-events-none disabled:tw-opacity-50"
              >
                {isLoading ? (
                  <>
                    <span
                      className="tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-white/40 tw-border-t-white"
                      aria-hidden="true"
                    />
                    Signing in...
                  </>
                ) : (
                  <>
                    <MicrosoftIcon />
                    Sign in with Microsoft
                  </>
                )}
              </button>
              {error && (
                <p
                  className="tw-mt-3 tw-text-center tw-font-sans tw-text-sm tw-text-destructive"
                  role="alert"
                >
                  {error}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(Login);
